import { CheckCircle2, ClipboardCheck, Table, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiChyba, hlaskaZChyby } from '../../api/client.js';
import { useVybranaZakazka } from '../../lib/globalniZakazka.js';
import type {
  ImportChyby422,
  ImportPreview,
  ImportSablona,
  ImportVysledek,
  Mapovani,
  Smlouva,
  Zakazka,
} from '../../api/types.js';
import { useAuth } from '../../auth/AuthContext.js';
import { formatKc, rozdilCastek } from '../../lib/format.js';
import { SMLOUVA_TYP_LABEL } from '../../lib/labels.js';
import { Btn, Callout, Chyba, Input, Pole, PoleKarta, Select, td, th } from '../../components/ui.js';

/**
 * Import soupisu prací — wizard dle wireframu 13 §5.
 *
 * Kroky:
 *  1. výběr zakázky/smlouvy + upload .xlsx (drag&drop i tlačítko)
 *     → POST /api/imports/preview (autodetekce mapování, listy, náhled řádků);
 *  2. mapování: select nad každým sloupcem náhledu (autodetekce předvyplní,
 *     nabídka uložených šablon), oddílové řádky „SO xx" zvýrazněné;
 *  3. validace a import: POST /api/imports — API validuje PŘED zápisem
 *     (422 = chyby s čísly řádků, nic se neuložilo); po úspěchu kontrolní
 *     součet Σ vs. cena SOD (zeleně sedí / žlutě nesedí s rozdílem);
 *  4. potvrzení: read-only ceník uložen + možnost uložit mapování jako šablonu.
 */

/** Pole modelu, na která se mapují sloupce (drátový formát ImportMapping.mapovani). */
const POLE_IMPORTU = ['kod', 'popis', 'mj', 'mnozstvi', 'jednotkovaCena', 'objekt', 'vrn'] as const;
type PoleImportu = (typeof POLE_IMPORTU)[number];

const POLE_LABEL: Record<PoleImportu, string> = {
  kod: 'Kód položky',
  popis: 'Popis',
  mj: 'MJ',
  mnozstvi: 'Množství',
  jednotkovaCena: 'Jedn. cena',
  objekt: 'Objekt (SO)',
  vrn: 'VRN',
};

const POVINNA_POLE: readonly PoleImportu[] = ['kod', 'popis', 'mj', 'mnozstvi', 'jednotkovaCena'];

/** Přiřazení sloupec → pole ('' = ignorovat). */
type SloupcePole = Record<string, PoleImportu | ''>;

/** Vzor oddílového řádku „SO xx" — shodný s API (apps/api imports/soupis.ts). */
const ODDIL_VZOR = /^SO\s*\d+[a-z]?\b/i;

/** Řazení písmen sloupců: A…Z, AA… (kratší dřív, pak abecedně). */
function porovnejSloupce(a: string, b: string): number {
  return a.length === b.length ? a.localeCompare(b) : a.length - b.length;
}

/** Z návrhu/šablony (pole → sloupec) udělá stav UI (sloupec → pole). */
function prirazeniZMapovani(mapovani: Partial<Mapovani>, sloupce: string[]): SloupcePole {
  const prirazeni: SloupcePole = {};
  for (const sloupec of sloupce) prirazeni[sloupec] = '';
  for (const pole of POLE_IMPORTU) {
    const sloupec = mapovani[pole];
    if (typeof sloupec === 'string' && sloupec in prirazeni) prirazeni[sloupec] = pole;
  }
  return prirazeni;
}

export function ImportWizard({
  initialZakazkaId = '',
  initialSmlouvaId = '',
  onHotovo,
}: {
  /** Předvyplnění při spuštění z detailu smlouvy (sekce Ceník). */
  initialZakazkaId?: string;
  initialSmlouvaId?: string;
  onHotovo: (batchId: string | null) => void;
}) {
  const { maFinance } = useAuth();
  const [krok, setKrok] = useState<1 | 2 | 3 | 4>(1);

  // Krok 1 — výběr zakázky/smlouvy + soubor (předvyplněno z deep-linku).
  const [zakazkaId, setZakazkaId] = useState(initialZakazkaId);
  const [smlouvaId, setSmlouvaId] = useState(initialSmlouvaId);
  const [soubor, setSoubor] = useState<File | null>(null);

  // Krok 2 — náhled + mapování.
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [prirazeni, setPrirazeni] = useState<SloupcePole>({});
  const [objektZOddilu, setObjektZOddilu] = useState(false);
  const [chybaMapovani, setChybaMapovani] = useState<string | null>(null);

  // Krok 3 — výsledek validace/importu.
  const [chyby422, setChyby422] = useState<ImportChyby422 | null>(null);
  const [vysledek, setVysledek] = useState<ImportVysledek | null>(null);

  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  // Předvyplnění zakázky z globálního přepínače (B1.2 / task bod 3).
  const { vybranaId } = useVybranaZakazka(zakazky);
  useEffect(() => {
    // jen jednorázové předvyplnění po načtení zakázek (závislost záměrně jen vybranaId)
    if (zakazkaId === '' && vybranaId) setZakazkaId(vybranaId);
  }, [vybranaId]);
  const { data: smlouvy } = useQuery({
    queryKey: ['smlouvy', zakazkaId],
    queryFn: () => api.get<Smlouva[]>(`/api/contracts?zakazkaId=${zakazkaId}`),
    enabled: zakazkaId !== '',
  });

  const sloupce = useMemo(() => {
    if (!preview) return [];
    const mnozina = new Set<string>(preview.hlavicka.map((h) => h.sloupec));
    for (const radek of preview.nahled) {
      for (const sloupec of Object.keys(radek.bunky)) mnozina.add(sloupec);
    }
    return [...mnozina].sort(porovnejSloupce);
  }, [preview]);

  const nahledPreview = useMutation({
    mutationFn: async (list?: string) => {
      const form = new FormData();
      form.append('soubor', soubor as File);
      form.append('nazevSouboru', (soubor as File).name);
      if (list) form.append('list', list);
      return api.upload<ImportPreview>('/api/imports/preview', form);
    },
    onSuccess: (data) => {
      setPreview(data);
      const sloupceData = new Set<string>(data.hlavicka.map((h) => h.sloupec));
      for (const radek of data.nahled) {
        for (const s of Object.keys(radek.bunky)) sloupceData.add(s);
      }
      setPrirazeni(prirazeniZMapovani(data.navrhMapovani, [...sloupceData]));
      setObjektZOddilu(data.navrhMapovani.objektZOddilu ?? false);
      setChybaMapovani(null);
      setKrok(2);
    },
  });

  /** Sestaví drátové mapování z přiřazení sloupců; null = chybí povinná pole. */
  const sestavMapovani = (): Mapovani | null => {
    const mapa: Partial<Record<PoleImportu, string>> = {};
    for (const [sloupec, pole] of Object.entries(prirazeni)) {
      if (pole !== '') mapa[pole] = sloupec;
    }
    const chybi = POVINNA_POLE.filter((p) => !mapa[p]);
    if (chybi.length > 0) {
      setChybaMapovani(
        `Přiřaďte všechna povinná pole — chybí: ${chybi.map((p) => POLE_LABEL[p]).join(', ')}.`,
      );
      return null;
    }
    return {
      kod: mapa.kod as string,
      popis: mapa.popis as string,
      mj: mapa.mj as string,
      mnozstvi: mapa.mnozstvi as string,
      jednotkovaCena: mapa.jednotkovaCena as string,
      ...(mapa.objekt ? { objekt: mapa.objekt } : {}),
      ...(mapa.vrn ? { vrn: mapa.vrn } : {}),
      objektZOddilu,
      hlavickaRadek: preview?.hlavickaRadek ?? 0,
    };
  };

  const importovat = useMutation({
    mutationFn: async () => {
      const mapovani = sestavMapovani();
      if (!mapovani || !preview) throw new ApiChyba(0, 'Mapování není kompletní.');
      const form = new FormData();
      form.append('soubor', soubor as File);
      form.append('nazevSouboru', (soubor as File).name);
      form.append('zakazkaId', zakazkaId);
      form.append('smlouvaId', smlouvaId);
      form.append('list', preview.list);
      form.append('mapovani', JSON.stringify(mapovani));
      return api.upload<ImportVysledek>('/api/imports', form);
    },
    onSuccess: (data) => {
      setVysledek(data);
      setChyby422(null);
    },
    onError: (err) => {
      if (err instanceof ApiChyba && err.status === 422) {
        setChyby422(err.telo as ImportChyby422);
      }
    },
  });

  const zakazka = zakazky?.find((z) => z.id === zakazkaId);
  const smlouva = smlouvy?.find((s) => s.id === smlouvaId);
  const popisSmlouvy = smlouva
    ? `${SMLOUVA_TYP_LABEL[smlouva.typ]}${smlouva.cislo ? ` ${smlouva.cislo}` : ''} — ${smlouva.protistrana}`
    : '';

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          Soupis prací (import)
          {zakazka
            ? ` — ${zakazka.nazev}${smlouva ? `, ${SMLOUVA_TYP_LABEL[smlouva.typ]}` : ''}`
            : ''}
        </h1>
      </div>
      <ol className="flex gap-2 text-sm">
        {['Soubor', 'Mapování', 'Validace', 'Potvrzení'].map((nazev, i) => (
          <li
            key={nazev}
            className={`rounded-full px-3 py-1 font-semibold ${
              krok === i + 1
                ? 'bg-primary text-white'
                : krok > i + 1
                  ? 'bg-success-bg text-success-text'
                  : 'bg-fill-disabled text-text-secondary'
            }`}
          >
            {i + 1}. {nazev}
          </li>
        ))}
      </ol>

      {krok === 1 && (
        <Krok1
          zakazky={zakazky}
          smlouvy={smlouvy}
          zakazkaId={zakazkaId}
          smlouvaId={smlouvaId}
          soubor={soubor}
          onZakazka={(id) => {
            setZakazkaId(id);
            setSmlouvaId('');
          }}
          onSmlouva={setSmlouvaId}
          onSoubor={setSoubor}
          chyba={nahledPreview.error ? hlaskaZChyby(nahledPreview.error) : null}
          nacitani={nahledPreview.isPending}
          onDalsi={() => nahledPreview.mutate(undefined)}
          onZrusit={() => onHotovo(null)}
        />
      )}

      {krok === 2 && preview && (
        <Krok2
          preview={preview}
          sloupce={sloupce}
          prirazeni={prirazeni}
          objektZOddilu={objektZOddilu}
          chyba={chybaMapovani}
          nacitamList={nahledPreview.isPending}
          onList={(list) => nahledPreview.mutate(list)}
          onSablona={(sablona) => {
            setPrirazeni(prirazeniZMapovani(sablona.mapovani, sloupce));
            setObjektZOddilu(sablona.mapovani.objektZOddilu ?? false);
            setChybaMapovani(null);
          }}
          onPrirazeni={(sloupec, pole) => {
            setPrirazeni((stare) => {
              const nove: SloupcePole = { ...stare };
              if (pole !== '') {
                // Jedno pole smí být jen na jednom sloupci (chová se jako přepínač).
                for (const klic of Object.keys(nove)) {
                  if (nove[klic] === pole) nove[klic] = '';
                }
              }
              nove[sloupec] = pole;
              return nove;
            });
            setChybaMapovani(null);
          }}
          onObjektZOddilu={setObjektZOddilu}
          onZpet={() => setKrok(1)}
          onDalsi={() => {
            if (sestavMapovani()) {
              setChyby422(null);
              setVysledek(null);
              importovat.reset();
              setKrok(3);
            }
          }}
        />
      )}

      {krok === 3 && (
        <Krok3
          maFinance={maFinance}
          popisZakazky={zakazka ? `${zakazka.cislo} — ${zakazka.nazev}` : ''}
          popisSmlouvy={popisSmlouvy}
          nazevSouboru={soubor?.name ?? ''}
          chyby422={chyby422}
          vysledek={vysledek}
          nacitani={importovat.isPending}
          jinaChyba={
            importovat.error &&
            !(importovat.error instanceof ApiChyba && importovat.error.status === 422)
              ? hlaskaZChyby(importovat.error)
              : null
          }
          onImport={() => importovat.mutate()}
          onZpet={() => setKrok(2)}
          onDalsi={() => setKrok(4)}
        />
      )}

      {krok === 4 && vysledek && (
        <Krok4 vysledek={vysledek} mapovani={sestavMapovani()} onHotovo={onHotovo} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Krok 1 — výběr zakázky/smlouvy + upload .xlsx
// ---------------------------------------------------------------------------

function Krok1(props: {
  zakazky?: Zakazka[];
  smlouvy?: Smlouva[];
  zakazkaId: string;
  smlouvaId: string;
  soubor: File | null;
  chyba: string | null;
  nacitani: boolean;
  onZakazka: (id: string) => void;
  onSmlouva: (id: string) => void;
  onSoubor: (soubor: File | null) => void;
  onDalsi: () => void;
  onZrusit: () => void;
}) {
  const [chybaSouboru, setChybaSouboru] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const vyberSoubor = (soubor: File | undefined) => {
    if (!soubor) return;
    if (!soubor.name.toLowerCase().endsWith('.xlsx')) {
      setChybaSouboru(`Soubor „${soubor.name}" není Excel (.xlsx) — vyberte export soupisu prací.`);
      return;
    }
    setChybaSouboru(null);
    props.onSoubor(soubor);
  };

  const pretazeni = (e: DragEvent) => {
    e.preventDefault();
    vyberSoubor(e.dataTransfer.files[0]);
  };

  const pripraveno = props.zakazkaId !== '' && props.smlouvaId !== '' && props.soubor !== null;

  return (
    <PoleKarta ikona={<Upload size={16} strokeWidth={1.75} />} nadpis="Zakázka, smlouva a soubor">
      <div className="grid max-w-2xl grid-cols-1 gap-4">
        <Pole popisek="Zakázka" povinne>
          <Select value={props.zakazkaId} onChange={(e) => props.onZakazka(e.target.value)}>
            <option value="">— vyberte zakázku —</option>
            {props.zakazky?.map((z) => (
              <option key={z.id} value={z.id}>
                {z.cislo} — {z.nazev}
              </option>
            ))}
          </Select>
        </Pole>
        <Pole popisek="Smlouva (ceník se váže na smlouvu)" povinne>
          <Select
            value={props.smlouvaId}
            onChange={(e) => props.onSmlouva(e.target.value)}
            disabled={props.zakazkaId === ''}
          >
            <option value="">
              {props.zakazkaId === '' ? '— nejdřív vyberte zakázku —' : '— vyberte smlouvu —'}
            </option>
            {props.smlouvy?.map((s) => (
              <option key={s.id} value={s.id}>
                {SMLOUVA_TYP_LABEL[s.typ]}
                {s.cislo ? ` ${s.cislo}` : ''} — {s.protistrana}
              </option>
            ))}
          </Select>
        </Pole>
        {/* Slepou uličku „bez smlouvy" nahrazuje hint s cestou zpět (blueprint 16 §6). */}
        {props.zakazkaId !== '' && props.smlouvy?.length === 0 && (
          <Chyba>
            Zakázka nemá žádnou smlouvu — ceník se váže na smlouvu. Nejdřív založte hlavní SOD v{' '}
            <Link
              to={`/zakazky/${props.zakazkaId}`}
              className="font-semibold underline underline-offset-2"
            >
              detailu zakázky
            </Link>
            .
          </Chyba>
        )}

        <div
          role="button"
          tabIndex={0}
          aria-label="Nahrát soubor .xlsx přetažením nebo výběrem"
          onDragOver={(e) => e.preventDefault()}
          onDrop={pretazeni}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
          className="flex min-h-36 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border-strong bg-bg p-6 text-center hover:border-primary hover:bg-primary-tint"
        >
          {props.soubor ? (
            <>
              <span className="text-lg font-semibold">{props.soubor.name}</span>
              <span className="text-text-secondary">
                Soubor připraven — pokračujte na mapování.
              </span>
            </>
          ) : (
            <>
              <span className="text-lg font-semibold">Přetáhněte sem soubor .xlsx</span>
              <span className="text-text-secondary">
                oceněný soupis prací (export z KROS, BuildPower…)
              </span>
            </>
          )}
          <Btn varianta="secondary" onClick={(e) => e.stopPropagation()}>
            <label className="cursor-pointer">
              Vybrat soubor
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx"
                className="sr-only"
                aria-label="Soubor se soupisem prací (.xlsx)"
                onChange={(e) => vyberSoubor(e.target.files?.[0])}
              />
            </label>
          </Btn>
        </div>
        {chybaSouboru && <Chyba>{chybaSouboru}</Chyba>}
        {props.chyba && <Chyba>{props.chyba}</Chyba>}

        <div className="flex gap-3">
          <Btn disabled={!pripraveno || props.nacitani} onClick={props.onDalsi}>
            {props.nacitani ? 'Načítám náhled…' : 'Pokračovat na mapování'}
          </Btn>
          <Btn varianta="subtle" onClick={props.onZrusit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </PoleKarta>
  );
}

// ---------------------------------------------------------------------------
// Krok 2 — mapování sloupců (selecty nad náhledem, šablony, oddíly „SO xx")
// ---------------------------------------------------------------------------

function Krok2(props: {
  preview: ImportPreview;
  sloupce: string[];
  prirazeni: SloupcePole;
  objektZOddilu: boolean;
  chyba: string | null;
  nacitamList: boolean;
  onList: (list: string) => void;
  onSablona: (sablona: ImportSablona) => void;
  onPrirazeni: (sloupec: string, pole: PoleImportu | '') => void;
  onObjektZOddilu: (hodnota: boolean) => void;
  onZpet: () => void;
  onDalsi: () => void;
}) {
  const { preview } = props;
  const nazvySloupcu = new Map(preview.hlavicka.map((h) => [h.sloupec, h.nazev]));
  const kodSloupec = Object.entries(props.prirazeni).find(([, p]) => p === 'kod')?.[0];
  const popisSloupec = Object.entries(props.prirazeni).find(([, p]) => p === 'popis')?.[0];
  const mnozstviSloupec = Object.entries(props.prirazeni).find(([, p]) => p === 'mnozstvi')?.[0];
  const cenaSloupec = Object.entries(props.prirazeni).find(([, p]) => p === 'jednotkovaCena')?.[0];

  /** Oddílový řádek „SO xx": vzor v kódu/popisu a prázdné množství i cena (jako API). */
  const jeOddil = (bunky: Record<string, string>): boolean => {
    const mnozstvi = (mnozstviSloupec ? (bunky[mnozstviSloupec] ?? '') : '').trim();
    const cena = (cenaSloupec ? (bunky[cenaSloupec] ?? '') : '').trim();
    if (mnozstvi !== '' || cena !== '') return false;
    const kandidati = [
      kodSloupec ? (bunky[kodSloupec] ?? '') : '',
      popisSloupec ? (bunky[popisSloupec] ?? '') : '',
    ];
    return kandidati.some((text) => ODDIL_VZOR.test(text.trim()));
  };

  return (
    <PoleKarta ikona={<Table size={16} strokeWidth={1.75} />} nadpis="Přiřaďte význam sloupcům">
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div className="text-text-secondary">
          Soubor: <strong>{preview.soubor ?? 'soupis.xlsx'}</strong>
        </div>
        {preview.listy.length > 1 && (
          <Pole popisek="List">
            <Select
              value={preview.list}
              disabled={props.nacitamList}
              onChange={(e) => props.onList(e.target.value)}
            >
              {preview.listy.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
          </Pole>
        )}
        {preview.sablony.length > 0 && (
          <Pole popisek="Šablona mapování">
            <Select
              defaultValue=""
              onChange={(e) => {
                const sablona = preview.sablony.find((s) => s.id === e.target.value);
                if (sablona) props.onSablona(sablona);
              }}
            >
              <option value="">— autodetekce —</option>
              {preview.sablony.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nazev}
                </option>
              ))}
            </Select>
          </Pole>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {props.sloupce.map((sloupec) => (
                <th key={sloupec} className={`${th} min-w-36`}>
                  <Select
                    aria-label={`Význam sloupce ${sloupec}`}
                    value={props.prirazeni[sloupec] ?? ''}
                    onChange={(e) => props.onPrirazeni(sloupec, e.target.value as PoleImportu | '')}
                    className={
                      props.prirazeni[sloupec]
                        ? 'border-primary font-semibold'
                        : 'text-text-secondary'
                    }
                  >
                    <option value="">— ignorovat —</option>
                    {POLE_IMPORTU.filter((p) => p !== 'objekt' || !props.objektZOddilu).map(
                      (pole) => (
                        <option key={pole} value={pole}>
                          {POLE_LABEL[pole]}
                        </option>
                      ),
                    )}
                  </Select>
                </th>
              ))}
            </tr>
            <tr>
              {props.sloupce.map((sloupec) => (
                <th key={sloupec} className={`${th} text-text-secondary`}>
                  {sloupec}
                  {nazvySloupcu.get(sloupec) ? ` · ${nazvySloupcu.get(sloupec)}` : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.nahled.map((radek) => {
              const oddil = props.objektZOddilu && jeOddil(radek.bunky);
              return (
                <tr key={radek.radek} className={oddil ? 'bg-warning-bg font-semibold' : ''}>
                  {props.sloupce.map((sloupec, i) => (
                    <td key={sloupec} className={`${td} text-sm`}>
                      {radek.bunky[sloupec] ?? ''}
                      {oddil && i === props.sloupce.length - 1 && (
                        <span className="ml-2 rounded-full bg-warning px-2 py-0.5 text-xs font-bold text-text-heading">
                          oddíl SO
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <fieldset className="mt-4">
        <legend className="mb-2 font-semibold">Objekt (SO):</legend>
        <div className="flex gap-6">
          <label className="flex min-h-11 items-center gap-2">
            <input
              type="radio"
              name="objekt-zdroj"
              className="size-5"
              checked={!props.objektZOddilu}
              onChange={() => props.onObjektZOddilu(false)}
            />
            vlastní sloupec
          </label>
          <label className="flex min-h-11 items-center gap-2">
            <input
              type="radio"
              name="objekt-zdroj"
              className="size-5"
              checked={props.objektZOddilu}
              onChange={() => props.onObjektZOddilu(true)}
            />
            oddílové řádky „SO xx"
            {props.preview.navrhMapovani.objektZOddilu ? ' (detekováno)' : ''}
          </label>
        </div>
      </fieldset>

      {props.chyba && (
        <div className="mt-4">
          <Chyba>{props.chyba}</Chyba>
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <Btn onClick={props.onDalsi}>Pokračovat na validaci</Btn>
        <Btn varianta="subtle" onClick={props.onZpet}>
          Zpět na soubor
        </Btn>
      </div>
    </PoleKarta>
  );
}

// ---------------------------------------------------------------------------
// Krok 3 — validace a import (API validuje před zápisem; 422 = nic se neuložilo)
// ---------------------------------------------------------------------------

function Krok3(props: {
  maFinance: boolean;
  popisZakazky: string;
  popisSmlouvy: string;
  nazevSouboru: string;
  chyby422: ImportChyby422 | null;
  vysledek: ImportVysledek | null;
  nacitani: boolean;
  jinaChyba: string | null;
  onImport: () => void;
  onZpet: () => void;
  onDalsi: () => void;
}) {
  const { vysledek, chyby422 } = props;

  return (
    <PoleKarta ikona={<ClipboardCheck size={16} strokeWidth={1.75} />} nadpis="Validace">
      <dl className="mb-4 grid grid-cols-3 gap-4">
        <div>
          <dt className="text-sm font-semibold text-text-secondary">Zakázka</dt>
          <dd>{props.popisZakazky}</dd>
        </div>
        <div>
          <dt className="text-sm font-semibold text-text-secondary">Smlouva</dt>
          <dd>{props.popisSmlouvy}</dd>
        </div>
        <div>
          <dt className="text-sm font-semibold text-text-secondary">Soubor</dt>
          <dd>{props.nazevSouboru}</dd>
        </div>
      </dl>

      {!vysledek && !chyby422 && (
        <>
          {/* Jedna věta výsledku; detail „co se kontroluje" za „podrobnosti ▾"
              (blueprint 16 §6 — odlehčit textové bloky). */}
          <p className="mb-2 text-text-primary">
            Zkontrolujeme celý soubor. Když projde, ceník se uloží; při chybách se neuloží nic.
          </p>
          <details className="mb-4 text-sm text-text-secondary">
            <summary className="cursor-pointer font-medium text-primary">podrobnosti</summary>
            <p className="mt-1">
              Kontrolujeme duplicitní kódy, formáty čísel a prázdné ceny. Ceník je po uložení
              read-only (smluvní opora ZL).
            </p>
          </details>
          {props.jinaChyba && (
            <div className="mb-4">
              <Chyba>{props.jinaChyba}</Chyba>
            </div>
          )}
          <div className="flex gap-3">
            <Btn disabled={props.nacitani} onClick={props.onImport}>
              {props.nacitani ? 'Kontroluji a importuji…' : 'Zkontrolovat a importovat'}
            </Btn>
            <Btn varianta="subtle" onClick={props.onZpet}>
              Zpět na mapování
            </Btn>
          </div>
        </>
      )}

      {chyby422 && (
        <div className="flex flex-col gap-4">
          <Chyba>
            <p className="mb-2 font-semibold">{chyby422.error}</p>
            <ul className="list-disc pl-5">
              {chyby422.chyby.map((ch) => (
                <li key={ch}>{ch}</li>
              ))}
            </ul>
          </Chyba>
          <Varovani varovani={chyby422.varovani} />
          <div className="flex gap-3">
            <Btn varianta="secondary" onClick={props.onZpet}>
              Zpět na mapování
            </Btn>
            <Btn disabled={props.nacitani} onClick={props.onImport}>
              Zkusit znovu
            </Btn>
          </div>
        </div>
      )}

      {vysledek && (
        <div className="flex flex-col gap-4">
          <KontrolniSoucet vysledek={vysledek} maFinance={props.maFinance} />
          <Varovani varovani={vysledek.varovani} />
          {vysledek.noveObjekty.length > 0 && (
            <p className="rounded-lg bg-info-bg p-3 text-info-text">
              Založeny nové objekty: {vysledek.noveObjekty.join(', ')}.
            </p>
          )}
          <div className="flex gap-3">
            <Btn onClick={props.onDalsi}>Pokračovat na potvrzení</Btn>
          </div>
        </div>
      )}
    </PoleKarta>
  );
}

function Varovani({ varovani }: { varovani: string[] }) {
  if (varovani.length === 0) return null;
  return (
    <Callout ton="warning">
      <ul className="list-disc pl-5">
        {varovani.map((v) => (
          <li key={v}>{v}</li>
        ))}
      </ul>
    </Callout>
  );
}

/** Kontrolní součet Σ vs. cena SOD — zeleně sedí / žlutě nesedí s rozdílem. */
function KontrolniSoucet({
  vysledek,
  maFinance,
}: {
  vysledek: ImportVysledek;
  maFinance: boolean;
}) {
  if (!maFinance || !vysledek.kontrolniSoucet || !vysledek.cenaSmlouvy) {
    return (
      <p className="rounded-lg bg-bg p-3 text-text-primary">
        Naimportováno {vysledek.pocetPolozek} položek. Kontrolní součet je viditelný jen s právem na
        finance.
      </p>
    );
  }
  if (vysledek.soucetOdpovidaCene) {
    return (
      <p className="rounded-lg border border-success-border bg-success-bg p-3 font-semibold text-success-text">
        Kontrolní součet sedí: Σ {formatKc(vysledek.kontrolniSoucet)} = cena SOD (netto bez DPH).
      </p>
    );
  }
  return (
    <p className="rounded-lg border border-warning-border bg-warning-bg p-3 font-semibold text-warning-text">
      Kontrolní součet nesedí: Σ {formatKc(vysledek.kontrolniSoucet)} vs. cena SOD{' '}
      {formatKc(vysledek.cenaSmlouvy)} — rozdíl{' '}
      {formatKc(rozdilCastek(vysledek.kontrolniSoucet, vysledek.cenaSmlouvy))}. Zkontrolujte soubor
      proti titulce SOD.
    </p>
  );
}

// ---------------------------------------------------------------------------
// Krok 4 — potvrzení: read-only ceník uložen + uložení šablony mapování
// ---------------------------------------------------------------------------

function Krok4({
  vysledek,
  mapovani,
  onHotovo,
}: {
  vysledek: ImportVysledek;
  mapovani: Mapovani | null;
  onHotovo: (batchId: string | null) => void;
}) {
  const [nazevSablony, setNazevSablony] = useState('');
  const ulozitSablonu = useMutation({
    mutationFn: () => api.post('/api/imports/mappings', { nazev: nazevSablony, mapovani }),
  });

  return (
    <PoleKarta ikona={<CheckCircle2 size={16} strokeWidth={1.75} />} nadpis="Potvrzení">
      <div className="flex flex-col gap-4">
        {/* Jedna věta výsledku; „co dál" za „podrobnosti ▾" (blueprint 16 §6). */}
        <p className="rounded-lg border border-success-border bg-success-bg p-4 text-success-text">
          <strong>Hotovo — ceník je uložen.</strong> Naimportováno {vysledek.pocetPolozek} položek
          do {vysledek.pocetObjektu} objektů
          {vysledek.radkuBezCeny > 0 ? `, ${vysledek.radkuBezCeny} řádků bez ceny („?")` : ''}.
        </p>
        <details className="text-sm text-text-secondary">
          <summary className="cursor-pointer font-medium text-primary">podrobnosti</summary>
          <p className="mt-1">
            Položky a ceny už nepůjde upravit (smluvní opora ZL). Nová verze soupisu = nový import,
            tato verze zůstane archivovaná.
          </p>
        </details>

        <div className="flex max-w-xl items-end gap-3">
          <Pole popisek="Uložit mapování jako šablonu (název)">
            <Input
              placeholder="např. KROS export (naše firma)"
              value={nazevSablony}
              onChange={(e) => setNazevSablony(e.target.value)}
            />
          </Pole>
          <Btn
            varianta="secondary"
            disabled={nazevSablony === '' || !mapovani || ulozitSablonu.isPending}
            onClick={() => ulozitSablonu.mutate()}
          >
            Uložit šablonu
          </Btn>
        </div>
        {ulozitSablonu.isSuccess && (
          <p className="text-success-text">
            Šablona „{nazevSablony}" uložena — příští import stejného formátu je na 1 klik.
          </p>
        )}
        {ulozitSablonu.error ? <Chyba>{hlaskaZChyby(ulozitSablonu.error)}</Chyba> : null}

        <div>
          <Btn onClick={() => onHotovo(vysledek.batchId)}>Hotovo — zpět na přehled importů</Btn>
        </div>
      </div>
    </PoleKarta>
  );
}
